/**
 * Reactive dependency extraction (ADR-0043 F1 · slice 1).
 *
 * The first brick of the reactive page runtime: given a `{{ }}` binding's
 * expression source, return WHICH scope paths it reads. A change to any of
 * those paths means the binding must re-evaluate; a binding that reads nothing
 * is constant. This is the data the dependency graph (a later slice) and the
 * Monaco intellisense are both built on — see the ADR ("intellisense is a
 * projection of the reactive graph").
 *
 * It walks the AST produced by the EXISTING, security-hardened evaluator
 * (`lib/pageExpr.ts`) — we do NOT add a second parser. So the grammar it
 * understands is exactly what the portal can evaluate (members, calls, arrow
 * functions, optional chaining, …), and it can never drift from it.
 *
 * Keystones of the extraction:
 *  - **Globals are not deps.** `{{ Math.round(x) }}` depends on `x`, never on
 *    `Math`. We skip any root identifier in `GLOBAL_NAMES` (derived from the
 *    evaluator's own `GLOBALS`).
 *  - **Arrow params shadow and are not deps.** In `rows.filter(r => r.won)`,
 *    `r` is bound by the arrow — the dep is `rows`, not `r`.
 *  - **A method call depends on its RECEIVER, not the method name.**
 *    `rows.filter(…)` → dep `["rows"]`, not `["rows","filter"]`.
 *  - **A computed segment truncates the static path** but its index expression
 *    contributes its own deps: `a[i].b` → deps `["a"]` + `["i"]`.
 *  - Paths are arrays so matching is prefix-based: a write to `$page.x` matches
 *    a binding that read `$page.x.y` (and vice-versa) — see {@link pathsOverlap}.
 */

import { GLOBAL_NAMES, parseExpr, templateExpressions, type Node } from "../pageExpr";

/** A static read-path into the scope, e.g. `["$page", "selectedRow"]`. */
export type DepPath = readonly string[];

/** Identifiers that resolve to a constant literal, never to the scope. */
const RESERVED = new Set(["true", "false", "null", "undefined"]);

/**
 * Resolve the static dotted path of a Member/Identifier chain rooted at a
 * scope read. Returns the path (e.g. `["$queries","orders","rows"]`) or `null`
 * when the chain doesn't bottom out at a scope identifier (a global / an
 * arrow-bound name / a literal). Side-effect: pushes any deps found inside
 * **computed** property expressions onto `out`, because those are evaluated
 * independently of the outer chain.
 */
function pathOf(node: Node, bound: Set<string>, out: string[][]): string[] | null {
  if (node.type === "Identifier") {
    const name = node.name ?? "";
    if (!name || RESERVED.has(name) || bound.has(name) || GLOBAL_NAMES.has(name)) {
      return null;
    }
    return [name];
  }
  if (node.type === "Member") {
    const base = node.object ? pathOf(node.object, bound, out) : null;
    if (node.computed) {
      // `a[expr]` — the index is its own little expression; collect its deps,
      // then the static path stops here (we can't know the dynamic key).
      if (node.property && typeof node.property !== "string") {
        walk(node.property, bound, out);
      }
      if (base) out.push(base);
      return null;
    }
    // `a.b` — extend the static path with the literal property name.
    const prop =
      typeof node.property === "string" ? node.property : (node.property?.name ?? "");
    if (base && prop) return [...base, prop];
    return null;
  }
  // Anything else (a call, a parenthesised expr, …) isn't a static chain —
  // walk it generically so its inner deps still surface.
  walk(node, bound, out);
  return null;
}

/** Recursively collect every scope read-path under `node` into `out`. */
function walk(node: Node | undefined, bound: Set<string>, out: string[][]): void {
  if (!node) return;
  switch (node.type) {
    case "Identifier":
    case "Member": {
      const p = pathOf(node, bound, out);
      if (p) out.push(p);
      return;
    }
    case "Literal":
      return;
    case "Unary":
      walk(node.argument, bound, out);
      return;
    case "Binary":
    case "Logical":
      walk(node.left, bound, out);
      walk(node.right, bound, out);
      return;
    case "Conditional":
      walk(node.test, bound, out);
      walk(node.consequent, bound, out);
      walk(node.alternate, bound, out);
      return;
    case "Array":
      for (const el of node.elements ?? []) walk(el, bound, out);
      return;
    case "Object":
      for (const p of node.properties ?? []) walk(p.value, bound, out);
      return;
    case "Call": {
      const callee = node.callee;
      if (callee && callee.type === "Member" && !callee.computed) {
        // Method call `x.y(…)` — the dep is the receiver `x.…`, NOT the method
        // name `y`. Walk the receiver chain; the method name is dropped.
        walk(callee.object, bound, out);
      } else if (callee) {
        // Bare call `fn(…)` (a global like SUM) — fn itself isn't a dep, but a
        // computed/odd callee still gets walked for safety.
        walk(callee, bound, out);
      }
      for (const a of node.args ?? []) walk(a, bound, out);
      return;
    }
    case "Arrow": {
      // Params SHADOW outer names inside the body.
      const inner = new Set(bound);
      for (const p of node.params ?? []) inner.add(p);
      walk(node.body, inner, out);
      return;
    }
  }
}

/** Stable join key for a path (paths never contain a NUL byte). */
const key = (p: DepPath): string => p.join(" ");

/**
 * Drop duplicates and any path that a strict ancestor already covers
 * (`$page` subsumes `$page.x`), since prefix-matching makes the longer one
 * redundant. Preserves first-seen order of the survivors.
 */
function normalize(paths: string[][]): DepPath[] {
  const seen = new Set<string>();
  const uniq: string[][] = [];
  for (const p of paths) {
    const k = key(p);
    if (seen.has(k)) continue;
    seen.add(k);
    uniq.push(p);
  }
  // Build the set of all keys to test ancestor membership cheaply.
  const keys = new Set(uniq.map(key));
  return uniq.filter((p) => {
    for (let i = 1; i < p.length; i++) {
      if (keys.has(key(p.slice(0, i)))) return false; // a strict prefix exists
    }
    return true;
  });
}

/**
 * Extract the distinct scope read-paths of a single binding expression
 * (the source INSIDE `{{ }}`, already unwrapped). A malformed expression has
 * no deps — it'll surface its error at eval time, not here. Pure + total.
 */
export function extractDeps(src: string): DepPath[] {
  let ast: Node;
  try {
    ast = parseExpr(src);
  } catch {
    return [];
  }
  const out: string[][] = [];
  walk(ast, new Set(), out);
  return normalize(out);
}

/**
 * Extract the distinct scope read-paths of a whole `{{ }}` TEMPLATE — the
 * union of every `{{ … }}` segment's deps. A brace-free string reads nothing.
 * This is the form page bindings actually take (`"Ciao {{ $user.name }}"`,
 * `"{{ $queries.x.rows }}"`), so the reactive engine uses this, not
 * {@link extractDeps} (which is the single-expression primitive).
 */
export function extractTemplateDeps(tpl: string): DepPath[] {
  const out: string[][] = [];
  for (const expr of templateExpressions(tpl)) {
    for (const p of extractDeps(expr)) out.push([...p]);
  }
  return normalize(out);
}

/**
 * Whether two paths overlap for change-propagation: one is a prefix of the
 * other (or they're equal). A write to `["$page","x"]` thus matches a read of
 * `["$page","x","y"]` AND a read of `["$page"]`. This is the matching rule the
 * dependency graph uses to decide what to recompute.
 */
export function pathsOverlap(a: DepPath, b: DepPath): boolean {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
